import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Mail, User, Briefcase, CheckCircle2, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const formatError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const ROLES = [
  { value: "travel_partner", label: "Travel Partner", description: "Hotels, destinations, airlines and tourism boards seeking certification" },
  { value: "corporate_member", label: "Corporate Member", description: "Hotel groups, airlines and cruise lines joining the Alliance" },
  { value: "government_member", label: "Government Member", description: "Ministries, tourism boards and destination management organisations" },
  { value: "advisory_member", label: "Advisory Member", description: "Academics, advisors and independent industry voices" },
  { value: "strategic_partner", label: "Strategic Partner", description: "Technology, ESG and hospitality service providers" },
  { value: "innovative_partner", label: "Innovative Partner", description: "Startups, scale-ups and sustainability ventures" },
  { value: "executive_board", label: "Executive Board", description: "Industry leaders and founding chairs of the Alliance" },
  { value: "sponsor", label: "Sponsor", description: "Brands and corporate sponsors of the WTTQ quality programme" },
  { value: "investor", label: "Investor", description: "Institutional or private investor in certified travel partners" },
  { value: "traveler", label: "Traveller", description: "Traveller discovering WTTQ-certified experiences" },
];

const SignInPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/dashboard";

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Register state
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regRole, setRegRole] = useState("travel_partner");
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success("Welcome back!");
      navigate(redirectTo);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      const data = await register({ email: regEmail, password: regPassword, full_name: regName, role: regRole });
      setRegSuccess(data);
      toast.success("Account created — awaiting admin approval");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setRegLoading(false);
    }
  };

  if (regSuccess) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" data-testid="signup-pending-page">
        <div className="max-w-md w-full bg-white border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 bg-[#FDF9EC] border-2 border-[#E8D38E] rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-[#92710d]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#0B132B] mb-3">Application Received</h1>
          <p className="text-[#4A5568] mb-2">
            Thanks, <strong>{regSuccess.user.full_name}</strong>.
          </p>
          <p className="text-[#4A5568] mb-6 leading-relaxed">
            Your account is <strong className="text-[#92710d]">pending admin approval</strong>.
            You'll be able to sign in once an admin reviews and approves your request.
          </p>
          <div className="bg-[#F2F4F8] border border-gray-200 rounded-md p-4 mb-6 text-left">
            <p className="text-xs uppercase tracking-wider text-[#718096] font-semibold mb-2">Submitted Details</p>
            <p className="text-sm text-[#4A5568]"><strong>Email:</strong> {regSuccess.user.email}</p>
            <p className="text-sm text-[#4A5568]"><strong>Role:</strong> {regSuccess.user.role}</p>
            <p className="text-sm text-[#4A5568]"><strong>Status:</strong> <span className="text-[#92710d] font-semibold">Pending</span></p>
          </div>
          <Button onClick={() => setRegSuccess(null)} className="w-full bg-[#0B132B] hover:bg-[#1C2541]" data-testid="pending-back-to-signin">
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="signin-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#0B132B]" data-testid="signin-back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Link to="/" className="font-heading text-xl font-bold">
            <span className="text-[#0B132B]">WTT</span><span className="text-[#D4AF37]">Q</span><span className="text-[#0B132B]">.org</span>
          </Link>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mb-3">Partner Portal</h1>
          <p className="text-[#4A5568] text-sm">
            Sign in or create an account to manage your WTTQ-certified properties.
          </p>
        </div>

        <div className="bg-white border border-gray-100 p-8">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-6 bg-[#F2F4F8]">
              <TabsTrigger value="signin" className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white" data-testid="tab-signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white" data-testid="tab-signup">Create Account</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleLogin} className="space-y-5" data-testid="signin-form">
                <div>
                  <Label htmlFor="login-email" className="flex items-center gap-2 mb-2 text-[#0B132B]">
                    <Mail className="w-4 h-4" /> Email
                  </Label>
                  <Input id="login-email" type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="you@example.com" data-testid="signin-email-input" />
                </div>
                <div>
                  <Label htmlFor="login-password" className="flex items-center gap-2 mb-2 text-[#0B132B]">
                    <Lock className="w-4 h-4" /> Password
                  </Label>
                  <Input id="login-password" type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" data-testid="signin-password-input" />
                </div>
                <Button type="submit" disabled={loginLoading} className="w-full bg-[#0B132B] hover:bg-[#1C2541] py-6" data-testid="signin-submit-button">
                  {loginLoading ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleRegister} className="space-y-5" data-testid="signup-form">
                <div>
                  <Label htmlFor="reg-name" className="flex items-center gap-2 mb-2 text-[#0B132B]">
                    <User className="w-4 h-4" /> Full Name
                  </Label>
                  <Input id="reg-name" required value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Jane Smith" data-testid="signup-name-input" />
                </div>
                <div>
                  <Label htmlFor="reg-email" className="flex items-center gap-2 mb-2 text-[#0B132B]">
                    <Mail className="w-4 h-4" /> Email
                  </Label>
                  <Input id="reg-email" type="email" required value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="you@example.com" data-testid="signup-email-input" />
                </div>
                <div>
                  <Label htmlFor="reg-password" className="flex items-center gap-2 mb-2 text-[#0B132B]">
                    <Lock className="w-4 h-4" /> Password
                  </Label>
                  <Input id="reg-password" type="password" required minLength={6} value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signup-password-input" />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2 text-[#0B132B]">
                    <Briefcase className="w-4 h-4" /> I am a…
                  </Label>
                  <Select value={regRole} onValueChange={setRegRole}>
                    <SelectTrigger data-testid="signup-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value} data-testid={`signup-role-${r.value}`}>
                          <div>
                            <div className="font-medium">{r.label}</div>
                            <div className="text-xs text-[#718096]">{r.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-start gap-2 text-xs text-[#718096] bg-[#F2F4F8] p-3 border border-gray-200 rounded-md">
                  <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>New accounts are reviewed by an admin. You'll be able to sign in once approved.</span>
                </div>
                <Button type="submit" disabled={regLoading} className="w-full bg-[#D4AF37] hover:bg-[#E8C456] text-[#0B132B] py-6 font-semibold" data-testid="signup-submit-button">
                  {regLoading ? "Creating account…" : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
